/**
 * ThreadSummonToggleButton — the "@Prest0n mention required" toggle as ONE
 * HeaderIconButton (header rail tiers, 2026-09-15).
 *
 * History: W3b (2026-08-06) shipped this control into the docked ⋯ overflow
 * only; live QA 2026-08-07 found fullscreen and floating had no way to see or
 * change the setting, so the wide rail gained this inline button. It is now
 * extracted so the COMPACT rail can mount the same control inline too — it
 * carries LIVE STATE (the gold "required" accent, the glyph swap) and belongs
 * where the eye lands, while the housekeeping buttons fold into ⋯.
 *
 * Same handler, same visibility rule (the host gates on `gatesThisThread`),
 * same hooks: `data-header-summon-toggle` carries the state, and the failure
 * notice rides the tooltip + `data-header-summon-error` — a setting that
 * silently refused to save must never look identical to one that saved (the
 * 2026-08-07 "turned gray again" report).
 *
 * Distinct glyph per state (owner feedback 2026-08-07): plain @ = mention
 * required; slashed @ = mention no longer required (replies to everything).
 * Mirrors the pin's filled/outline pattern — state lives in the ICON, with
 * the accent styling on [data-header-summon-toggle='required'] on top.
 */
import React from 'react';
import { HeaderIconButton } from './styles';
import { MentionOffSvgIcon, MentionSvgIcon } from './components';

export interface ThreadSummonToggleButtonProps {
  /** Current setting; `undefined` reads as required (the toggle's own default). */
  requireMention: boolean | undefined;
  /** Flip the setting — the SAME setter every other summon control calls. */
  onSetRequireMention: (next: boolean) => void;
  /** A rejected setting write; rides the tooltip so a failed save is never silent. */
  notice?: string | null;
}

export const ThreadSummonToggleButton: React.FC<ThreadSummonToggleButtonProps> = ({
  requireMention,
  onSetRequireMention,
  notice,
}) => {
  const required = requireMention !== false;
  return (
    <HeaderIconButton
      onClick={() => onSetRequireMention(!required)}
      data-header-summon-toggle={required ? 'required' : 'always'}
      aria-pressed={required}
      aria-label="require an @Prest0n mention for Prest0n to reply in this thread"
      title={
        notice
          ? notice
          : required
            ? 'Prest0n replies only when mentioned'
            : 'Prest0n replies to every message'
      }
      data-header-summon-error={notice ? '' : undefined}
    >
      {required
        ? <MentionSvgIcon />
        : <MentionOffSvgIcon />}
    </HeaderIconButton>
  );
};

export default ThreadSummonToggleButton;
